import React, { useEffect, useRef, useState } from "react";
import styles from "./LienProcess.module.scss";
import UtilityBar from "./UtilityBar";
import Form from "../Form";
import CustomTableWithPagination from "../../reusable-components/CustomTable";
import ConditionalRender from "../../reusable-components/ConditionalRender";
import {
  getLastSyncedInfo,
  getPageKeys,
  lienColumns,
  lienProcessConsts,
  STATIC_ALERT,
  syncSummaryValues,
  warningNotificationStyle,
} from "./constants";
import BlueInfo from "../../assets/images/blueInfo.svg";
import AlertIcon from "../../assets/images/warningAlert.svg";
import LienProcessingService from "../../services/lienProcess.service";
import { message, Modal, notification } from "antd";
import {
  ButtonShape,
  ButtonType,
  CommonButtonTexts,
} from "../../enums/ButtonAttributesEnums";
import TNButton from "../../reusable-components/TNButton";
import TNLoader from "../../reusable-components/TNLoader";
import SocketLoader from "../../reusable-components/SocketLoader";
import { socketActionType } from "../../enums/socketEnums";
import { syncSummaryType } from "../../Types/lienProcessingTypes";
import { directDownloadUtil } from "../../utils/downloadUtil";
import { storageKeys } from "../../constants/storageKeys";
import RolesServices from "../../services/roles.services";
import { generatePath, useHistory, useLocation } from "react-router-dom";
import { appRoutes } from "../../constants/appRoutes";
import { useLienProcessFilterContext } from "../../context/LienProcessFilterContext";
import JobDatabaseService from "../../services/jobDatabase.service";
import { ApiResponse } from "../../enums/ApiResponse";
import { TableDefaults } from "../../enums/TableEnums";

const LienProcess = () => {
  const isMountRef = useRef<boolean>(true);
  const {
    FetchLienProcessDashboard,
    SyncWithQuickbooks,
    DownloadAllLienDocuments,
    ExportNonSyncedRecords,
    DownloadSingleEntryBatch,
  } = LienProcessingService();
  const { GetPaginationDefaultMeta } = JobDatabaseService();
  const navigate = useHistory();
  const location = useLocation();
  const userDetails = localStorage.getItem(storageKeys.LS_USER_DETAILS);
  const userDetailsJson = JSON.parse(userDetails);
  const userRoleInfo = userDetailsJson?.[lienProcessConsts.ROLE_DETAILS_KEY];
  const userRoleProfileInfo =
    userDetailsJson?.[lienProcessConsts.PROFILE_DETAILS_KEY];
  const { data: defaultData, isFetching: gettingDefaults ,isSuccess} = GetPaginationDefaultMeta();
  const { FetchUserRoleDetails } = RolesServices();
  const { data: roleDetails } = FetchUserRoleDetails(userRoleInfo);

  const editAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[3]?.selectedOptions?.includes(
      lienProcessConsts.EDIT_ROLE
    )
    : true;

  const { filters, updateFilters } = useLienProcessFilterContext();
  const [pageSize, setPageSize] = useState<number | null>(() => {
    const stored = sessionStorage.getItem(storageKeys.LIEN_PAGESIZE);
    return stored ? Number(stored) : null;
  });
  const [pageSizeChanged, setPageSizeChanged] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [syncSummaryDetails, setSyncSummaryDetails] = useState<syncSummaryType>(
    {
      totalInvoices: 0,
      syncedCount: 0,
      unsyncedCount: 0,
    }
  );
  const [isConfirmSyncOpen, setIsConfirmSyncOpen] = useState<boolean>(false);
  const [isSyncResultOpen, setIsSyncResultOpen] = useState<boolean>(false);

  const [downloadAction, setDownloadAction] = useState(false);
  const [syncAction, setSyncAction] = useState(false);


  const {
    data: dataList,
    refetch,
    isPending,
    isFetching,
    isRefetching,
    isLoading,
    isSuccess: leinFetchedSuccess,
  } = FetchLienProcessDashboard({
    page: filters?.page ?? 1,
    perPage: pageSize,
    companyId: filters?.customerFilter ?? [],
    jobId: filters?.jobFilter ?? [],
    month: filters?.monthFilter ?? [],
  }, pageSizeChanged, !!sessionStorage.getItem(storageKeys.LIEN_PAGESIZE) && pageSize !== null);


  const handlePageChange = (current: number) => {
    updateFilters({ page: current });
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageSizeChanged(true);
    updateFilters({ page: 1 });    
    try {
      sessionStorage.setItem(storageKeys.LIEN_PAGESIZE, size);
    } catch(err) {
      console.error("Err", err);
    }
  };

  const { mutateAsync: syncQuickbooksCall } = SyncWithQuickbooks();
  const { mutateAsync: downloadAllCall } = DownloadAllLienDocuments();
  const {
    mutateAsync: exportNonSyncedCall,
    isPending: exportNonSyncedPending,
  } = ExportNonSyncedRecords();
  const {
    mutateAsync: downloadSingleBatch,
    isPending: downloadSingleBatchPending,
  } = DownloadSingleEntryBatch();

  const handleSyncQuickbooks = async () => {
    const response = await syncQuickbooksCall();
    return response;
  };

  const handleDownloadAllCall = async () => {
    const response = await downloadAllCall({
      batchIds: selectedRowKeys ?? [],
    });
    return response;
  };

  const handleExportNonSynced = async () => {
    const data = await exportNonSyncedCall();
    directDownloadUtil(data?.url);
  };

  const handleStartDownloadAction = () => setDownloadAction(true);

  const handleEndDownloadAction = () => setDownloadAction(false);

  const handleStartSyncAction = () => {
    handleConfirmSyncClose();
    setSyncAction(true);
  };

  const handleEndSyncAction = () => setSyncAction(false);

  const handleConfirmSyncOpen = () => setIsConfirmSyncOpen(true);
  const handleConfirmSyncClose = () => setIsConfirmSyncOpen(false);

  const handleSyncResultOpen = () => setIsSyncResultOpen(true);
  const handleSyncResultClose = () => setIsSyncResultOpen(false);

  const handleDownloadAll = () => {
    handleStartDownloadAction();
  };
  const handleDownloadSuccess = () => {
    refetch();
  };

  const handleDownloadSingleBatch = async (batchId: string) => {
    const data = await downloadSingleBatch(batchId, {
      onSuccess: (res) => {
        directDownloadUtil(res?.zipUrl);
      },
      onError: (err) => {
        const errorResponse = err?.response;
        message.warning(errorResponse?.data?.message);
      }
    });
  };

  const updateSyncSummaryDetails = (data: syncSummaryType) => {
    setSyncSummaryDetails({
      totalInvoices: data?.totalInvoices,
      syncedCount: data?.syncedCount,
      unsyncedCount: data?.unsyncedCount,
    });
    refetch();
    handleSyncResultOpen();
  };

  const handleViewNavigate = (record?: any) => {
    const recordName = {
      lienRecordName: `${record?.jobName} - ${record?.month}`,
    };
    navigate.push(
      generatePath(appRoutes.LIEN_DETAIL_VIEW, { id: record?.id }),
      recordName
    );
  };

  const handleRowSelectionChange = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const showTop20Warning = () => {
    notification.warn({
      icon: <img className={styles.warningIcon} src={AlertIcon} />,
      className: styles.warningNotificationStyle,
      style: warningNotificationStyle,
      message: lienProcessConsts.TOP_20_WARNING,
      duration: 4,
      closeIcon: <></>,
    });
  };

  const rowSelection = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys.slice(0, lienProcessConsts.MAX_SELECTION));
    },

    getCheckboxProps: (record) => ({
      disabled:
        selectedRowKeys.length >= lienProcessConsts.MAX_SELECTION &&
        !selectedRowKeys.includes(record.id),
    }),

    onSelectAll: (selected, selectedRows, changeRows) => {
      const pageKeys = getPageKeys(changeRows);
      const perPage = dataList?.meta?.perPage ?? pageSize ?? TableDefaults.PAGESIZE;

      if (!selected) {
        setSelectedRowKeys(prev =>
          prev.filter(k => !pageKeys.includes(k))
        );
        return;
      }

      if (perPage >= lienProcessConsts.MAX_SELECTION) {
        showTop20Warning();
        setSelectedRowKeys(pageKeys.slice(0, lienProcessConsts.MAX_SELECTION));
        return;
      }

      const remainingSlots = lienProcessConsts.MAX_SELECTION - selectedRowKeys.length;
      if (remainingSlots <= 0) return;

      const keysToAdd = pageKeys
        .filter(k => !selectedRowKeys.includes(k))
        .slice(0, remainingSlots);

      setSelectedRowKeys(prev => [...prev, ...keysToAdd]);
    },
  };

  useEffect(() => {
    if(defaultData?.message && defaultData?.message === ApiResponse.SUCCESS) {
      if(!!!pageSize)
        setPageSize(defaultData?.data?.lienDefaultPageSize);
      if(!!!sessionStorage.getItem(storageKeys.LIEN_PAGESIZE))
        sessionStorage.setItem(storageKeys.LIEN_PAGESIZE, defaultData?.data?.lienDefaultPageSize);
    }
  }, [defaultData])

  useEffect(() => {
    if(pageSizeChanged && leinFetchedSuccess) {
      setPageSizeChanged(false);
    }
  }, [pageSizeChanged, leinFetchedSuccess])

  return (
    <>
      <ConditionalRender visible={downloadAction}>
        <SocketLoader
          actionType={socketActionType.DOWNLOAD_ALL_UNPAID}
          closeRender={handleEndDownloadAction}
          handleCallback={handleDownloadSuccess}
          handleActionCall={handleDownloadAllCall}
        />
      </ConditionalRender>
      <ConditionalRender visible={syncAction}>
        <SocketLoader
          actionType={socketActionType.SYNC_QUICKBOOKS}
          closeRender={handleEndSyncAction}
          handleActionCall={handleSyncQuickbooks}
          handleCallback={updateSyncSummaryDetails}
        />
      </ConditionalRender>
      <Form>
        {() => (
          <UtilityBar
            editAccess={editAccess}
            selectedRowsKeyLength={selectedRowKeys?.length}
            handleSyncConfirm={handleConfirmSyncOpen}
            handleDownloadAll={handleDownloadAll}
            handleExportUnsynced={handleExportNonSynced}
          />
        )}
      </Form>

      <Modal
        visible={isConfirmSyncOpen}
        title={lienProcessConsts.CONFIRM_SYNC_TITLE}
        onCancel={handleConfirmSyncClose}
        onOk={handleStartSyncAction}
        maskClosable={false}
        okText={CommonButtonTexts.CONFIRM}
        width={lienProcessConsts.CONFIRM_SYNC_WIDTH}
      >
        {lienProcessConsts.CONFIRM_SYNC_TEXT}
      </Modal>
      <Modal
        visible={isSyncResultOpen}
        title={lienProcessConsts.SYNC_RESULT_TITLE}
        onCancel={handleSyncResultClose}
        maskClosable={false}
        width={lienProcessConsts.SYNC_RESULT_WIDTH}
        footer={
          <TNButton
            type={ButtonType.DEFAULT}
            shape={ButtonShape.DEFAULT}
            text={lienProcessConsts.EXPORT_NON_SYNCED}
            onClick={() => {
              handleSyncResultClose();
              handleExportNonSynced();
            }}
          />
        }
      >
        <div className={styles.summaryContainer}>
          {syncSummaryValues(syncSummaryDetails).map((item) => (
            <div className={styles.summaryDetail}>
              <div className={styles.titleText}>{item.title}</div>
              <div className={styles.valueText}>{item.value}</div>
              <div className={styles.labelText}>{item.label}</div>
            </div>
          ))}
        </div>
      </Modal>
      <TNLoader
        isLoading={
          isPending ||
          isFetching ||
          isLoading ||
          isRefetching ||
          exportNonSyncedPending ||
          downloadSingleBatchPending
        }
      />
      <ConditionalRender
        visible={dataList?.data?.length}
        fallback={
          <div className={styles.emptyTable}>
            {lienProcessConsts.EMPTY_TABLE_MSG}
          </div>
        }
      >
        <CustomTableWithPagination
          dataSource={dataList?.data ?? []}
          columns={lienColumns({
            handleDownloadClick: handleDownloadSingleBatch,
            editAccess: editAccess,
          })}
          rowSelection={rowSelection}
          rowKey={(record) => record?.id}
          footer={() => (
            <div className={styles.footerContainer}>
              <div className={styles.staticMessage}>
                <img src={BlueInfo} />
                <span>{STATIC_ALERT}</span>
              </div>
              <div className={styles.lastSynced}>
                {getLastSyncedInfo(dataList?.syncDetails)}
              </div>
            </div>
          )}
          isClickableTable
          handleSelectedRow={handleViewNavigate}
          totalData={dataList?.meta?.totalRecords}
          currentPage={filters?.page ?? 1}
          pageSize={pageSize ?? TableDefaults.PAGESIZE}
          handleChange={handlePageChange}
          handleSizeChangerChange={handlePageSizeChange}
        />
      </ConditionalRender>
    </>
  );
};

export default LienProcess;
